// Sign in · Create account · Forgot password — with the game account (email + password), through the Worker.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { GamePromptError } from "@/api/http";
import { art } from "@/kit/art";
import { Flourish } from "@/kit/icons";
import { Button, Card, Field } from "@/kit/primitives";
import { Shell } from "@/kit/chrome";
import { keys } from "@/state/queries";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import "./signin.css";

type Mode = "signin" | "register" | "recover";

export function SignIn() {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const setSignedIn = useSession((s) => s.setSignedIn);
  const prompts = usePrompts();

  const switchTo = (m: Mode) => {
    setMode(m);
    setErr(null);
    setSent(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return setErr("Enter the email of your Valor account.");
    if (mode !== "recover" && password.length < 8) return setErr("Your password has at least 8 characters.");
    if (mode === "register" && password !== password2) return setErr("The two passwords don't match.");
    setBusy(true);
    try {
      if (mode === "recover") {
        await api.session.recover(em);
        setSent(true);
        return;
      }
      const r = mode === "signin" ? await api.session.login(em, password) : await api.session.register(em, password);
      if (!r.ok || !r.uid) {
        setErr(r.Error || "The game didn't accept that.");
        return;
      }
      if (r.init) qc.setQueryData(keys.init, r.init);
      setSignedIn({ uid: r.uid, login: r.login, email: r.email });
      const inWorlds = r.init?.world?.worlds_in?.length ?? 0;
      nav(inWorlds ? "/portal" : "/worlds", { replace: true });
    } catch (ex) {
      if (ex instanceof GamePromptError) {
        const general = ex.prompts.find((p) => p.prompt_type === "prompt_general");
        setErr(general?.text || ex.message);
        if (!general) prompts.error(ex);
      } else setErr("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell className="signin pg-col">
      <div className="signin-top">
        <div className="toprule" />
        <div className="wordmark-row">
          <Flourish mirror className="fl" />
          <h1 className="valor">VALOR</h1>
          <Flourish className="fl" />
        </div>
        <div className="tagline">Forge your legend</div>
      </div>
      <Card bare className="signin-hero hero full">
        <div className="art" style={{ ["--art-w" as string]: "100%" }}>
          <img src={art.hero.home} alt="" decoding="async" />
        </div>
        <div className="body">
          <h2>{mode === "signin" ? "Welcome back" : mode === "register" ? "Begin your legend" : "Lost your key?"}</h2>
          <p>
            {mode === "signin"
              ? "Sign in with your Valor account. Your worlds, cities and gold are the same here as in the app."
              : mode === "register"
                ? "One account plays every world, in the app and in the browser."
                : "We'll email you a link to set a new password."}
          </p>
        </div>
      </Card>
      <Card className="signin-card">
        {sent ? (
          <>
            <p className="hint" style={{ fontSize: "var(--fs-body)", color: "var(--cream)" }}>
              If that email has a Valor account, a reset link is on its way. Check your inbox (and spam), then come back and sign in.
            </p>
            <Button kind="steel" wide onClick={() => switchTo("signin")}>Back to sign in</Button>
          </>
        ) : (
          <form onSubmit={submit} className="stack" style={{ gap: "calc(var(--u) * 20)" }}>
            <Field label="Email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            {mode !== "recover" && (
              <Field label="Password" type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" required />
            )}
            {mode === "register" && (
              <Field label="Password again" type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} required />
            )}
            {err && <div className="hint err" role="alert">{err}</div>}
            <Button kind="crimson" wide type="submit" busy={busy}>
              {mode === "signin" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
            <div className="signin-links">
              {mode !== "signin" && <button type="button" className="link" onClick={() => switchTo("signin")}>Sign in</button>}
              {mode !== "register" && <button type="button" className="link" onClick={() => switchTo("register")}>Create account</button>}
              {mode !== "recover" && <button type="button" className="link" onClick={() => switchTo("recover")}>Forgot password</button>}
            </div>
          </form>
        )}
      </Card>
      <p className="signin-foot">Valor Web is the browser version of Valor Reforged. Gold is bought in the app and appears here.</p>
    </Shell>
  );
}
